import { ClickAwayListener, Popper } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import {
  ScreenType,
  useScreenType,
  WMS_MOBILE_MAX_WIDTH,
} from "@redotech/react-util/screen";
import { useTranslation } from "@redotech/redo-i18n";
import CalendarIcon from "@redotech/redo-icons/calendar-date.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { getDateString } from "@redotech/util/date-format";
import classNames from "classnames";
import { memo, useMemo, useState } from "react";
import { RedoButton, RedoButtonSize } from "../buttons/redo-button";
import { Flex } from "../flex";
import { DatePicker } from "./date-picker/date-picker-menu";
import * as styles from "./redo-single-date-picker.module.css";

export interface RedoSingleDatePickerProps {
  "data-dd-action-name"?: string;
  date: Temporal.PlainDate | undefined;
  disabled?: boolean;
  setDate: (date: Temporal.PlainDate) => void;
  size?: RedoButtonSize;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  allowNoDateOption?: boolean;
  dateStringOptions?: Pick<
    Intl.DateTimeFormatOptions,
    "year" | "month" | "day"
  >;
  /** Show Active/Expired badge based on whether date is in future or past */
  showStatus?: boolean;
  /** When true, the calendar shows month + year dropdowns for jumping between months. Defaults to false. */
  showMonthYearDropdowns?: boolean;
}

export const RedoSingleDatePicker = memo(function RedoSingleDatePicker({
  date,
  disabled,
  setDate,
  minDate,
  maxDate,
  size = "sm",
  allowNoDateOption = false,
  dateStringOptions = {},
  showStatus = false,
  showMonthYearDropdowns = false,
  "data-dd-action-name": actionName,
}: RedoSingleDatePickerProps) {
  const { i18n, t } = useTranslation();
  const screenType = useScreenType(WMS_MOBILE_MAX_WIDTH);
  const isMobile = screenType === ScreenType.MOBILE;
  const buttonDisplay = date
    ? getDateString(date, { ...dateStringOptions, locale: i18n.language })
    : t("Select date", { context: "date picker" });

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const [pickerOpen, setPickerOpen] = useState(false);

  const status = useMemo(() => {
    if (!showStatus || !date) {
      return null;
    }
    const today = Temporal.Now.plainDateISO();
    return Temporal.PlainDate.compare(date, today) > 0 ? "active" : "expired";
  }, [showStatus, date]);

  const handleSetCustomDate = useHandler((value: Temporal.PlainDate | null) => {
    if (!value) {
      return;
    }
    setDate(value);
    setPickerOpen(false);
  });

  const handleClick = useHandler((e: React.MouseEvent) => {
    e.stopPropagation();
    setPickerOpen((open) => !open);
  });

  const statusChip = status && (
    <div
      className={classNames(
        styles.statusChip,
        status === "active" && styles.active,
        status === "expired" && styles.expired,
      )}
    >
      {status === "active"
        ? t("Active", { context: "date status" })
        : t("Expired", { context: "date status" })}
    </div>
  );

  return (
    <>
      <Flex
        align="center"
        className={classNames(styles.container, status && styles.hasStatus)}
        data-arbiter="RedoSingleDatePicker"
        data-dd-action-name={actionName}
        gap="none"
      >
        {statusChip}
        <RedoButton
          centerItems={false}
          data-dd-action-name={actionName}
          disabled={disabled}
          hierarchy="secondary"
          IconLeading={CalendarIcon}
          onClick={handleClick}
          ref={setDropdownAnchor}
          size={size}
          text={buttonDisplay}
        />
      </Flex>
      <ClickAwayListener onClickAway={() => setPickerOpen(false)}>
        <Popper
          anchorEl={dropdownAnchor}
          data-dd-action-name={actionName}
          modifiers={[
            { name: "offset", options: { offset: [0, 8] } },
            { name: "flip", enabled: true },
            {
              name: "preventOverflow",
              options: { altAxis: true, tether: false, padding: 8 },
            },
          ]}
          open={pickerOpen}
          placement="bottom-start"
        >
          <Flex
            dir="column"
            gap="none"
            maxHeight="screen"
            overflowY="auto"
            p="xs"
            style={{ position: "relative" }}
          >
            {isMobile && (
              <div
                style={{ position: "absolute", top: 12, right: 12, zIndex: 1 }}
              >
                <RedoButton
                  aria-label={t("Close date picker", {
                    context: "date picker",
                  })}
                  hierarchy="tertiary"
                  IconLeading={XIcon}
                  onClick={() => setPickerOpen(false)}
                  size="sm"
                />
              </div>
            )}
            <DatePicker
              allowNoDateOption={allowNoDateOption}
              maxDate={maxDate}
              minDate={minDate}
              onDateChange={handleSetCustomDate}
              selectedDate={date || null}
              showMonthYearDropdowns={showMonthYearDropdowns}
            />
          </Flex>
        </Popper>
      </ClickAwayListener>
    </>
  );
});
